export const CF_WORKER_ANNOTATION = 'cloudflare.com/worker-name';

/** Predicate for EntityLayout.Route — shows the tab only when the annotation is present. */
export const isCloudflareWorkerAvailable = (entity: {
  metadata: { annotations?: Record<string, string> };
}) => Boolean(entity.metadata?.annotations?.[CF_WORKER_ANNOTATION]);
export const SOURCE_LOCATION_ANNOTATION = 'backstage.io/source-location';
export const INTERNAL_EXPERIMENTS_REPO_URL =
  'https://github.com/integromat/internal-experiments';

/** The vibe-coding platform guide site (itself a vibe-coded app). */
export const VIBE_GUIDE_URL = 'https://vibe-coding-platform-guide.app.mkhq.io/';
/** "Start from scratch" section of the guide — where to build a new app. */
export const VIBE_GUIDE_NEW_APP_URL = `${VIBE_GUIDE_URL}#new-app`;

/** Curated showcase of internal apps (Spotlight). */
export const SPOTLIGHT_URL = 'https://spotlight.app.mkhq.io';
export const spotlightAppUrl = (directory: string) =>
  `${SPOTLIGHT_URL}/apps/${encodeURIComponent(directory)}`;
export const spotlightCategoryUrl = (category: string) =>
  `${SPOTLIGHT_URL}/apps?category=${encodeURIComponent(category)}`;

/** Categories from `apps/spotlight/src/content/schema.ts` (SCHEMA_VERSION 1). */
export const SPOTLIGHT_CATEGORIES = [
  { id: 'product-design', label: 'Product & design' },
  { id: 'engineering', label: 'Engineering & platform' },
  { id: 'data-reports', label: 'Data & reports' },
  { id: 'workplace', label: 'Workplace & people' },
  { id: 'sales-gtm', label: 'Sales & GTM' },
  { id: 'events-marketing', label: 'Events & marketing' },
  { id: 'finance-pricing', label: 'Finance & pricing' },
  { id: 'reference', label: 'Reference implementations' },
] as const;

export type SpotlightCategoryId = (typeof SPOTLIGHT_CATEGORIES)[number]['id'];

export function spotlightCategoryLabel(
  category: string | null | undefined,
): string | null {
  if (!category) return null;
  return SPOTLIGHT_CATEGORIES.find(c => c.id === category)?.label ?? category;
}

/** Capability labels from `apps/spotlight/src/content/schema.ts`. */
export const SPOTLIGHT_CAPABILITY_LABELS: Readonly<Record<string, string>> = {
  d1: 'D1 database',
  kv: 'KV storage',
  r2: 'R2 file storage',
  cron: 'Scheduled jobs',
  queues: 'Queues',
  'durable-objects': 'Durable Objects',
  'workers-ai': 'Workers AI',
  llm: 'LLM calls',
  'sso-identity': 'SSO identity',
  'make-webhooks': 'Make webhooks',
  'make-api': 'Make API',
  charts: 'Charts',
  diagrams: 'Diagrams & flows',
  'drag-and-drop': 'Drag and drop',
  markdown: 'Markdown',
  pdf: 'PDF',
  spreadsheets: 'Spreadsheets & CSV',
  maps: 'Maps',
  animation: 'Animation',
  orm: 'ORM',
  airtable: 'Airtable',
  supabase: 'Supabase',
  'google-apis': 'Google APIs',
  slack: 'Slack',
  'github-api': 'GitHub API',
};

export function spotlightCapabilityLabel(id: string): string {
  return SPOTLIGHT_CAPABILITY_LABELS[id] ?? id;
}

/** Scaffolder path for the "Update a Vibe Coded App" template. */
export const VIBE_UPDATE_TEMPLATE_PATH =
  '/create/templates/default/update-vibe-coded-app';

/** Lowercase the kind portion of an entity ref (the templates strip a lowercase prefix). */
export function toLowerKindRef(ref: string): string {
  const i = ref.indexOf(':');
  return i === -1 ? ref : `${ref.slice(0, i).toLowerCase()}${ref.slice(i)}`;
}

/**
 * Build the link to the "Update a Vibe Coded App" template, preselecting the
 * given app via the EntityPicker when its catalog ref is known. The kind is
 * lowercased so the prefilled value matches the canonical ref the picker emits.
 */
export function vibeUpdateUrl(entityRef?: string | null): string {
  if (!entityRef) return VIBE_UPDATE_TEMPLATE_PATH;
  const formData = encodeURIComponent(
    JSON.stringify({ appEntity: toLowerKindRef(entityRef) }),
  );
  return `${VIBE_UPDATE_TEMPLATE_PATH}?formData=${formData}`;
}

export function buildEntityRef(entity: {
  kind: string;
  metadata: { name: string; namespace?: string | null };
}): string {
  return `${entity.kind}:${entity.metadata.namespace ?? 'default'}/${
    entity.metadata.name
  }`;
}

/** Build a Backstage catalog URL path from an entityRef like "Component:default/my-worker" */
export function entityRefToUrl(entityRef: string): string {
  const [kindAndNamespace, name] = entityRef.split('/');
  const [kind, namespace] = kindAndNamespace.split(':');
  return `/catalog/${namespace}/${kind.toLowerCase()}/${name}`;
}

/**
 * Build a Backstage catalog URL path from an owner ref like
 * "group:default/devex", "group:devex", or "user:default/john".
 * Defaults to kind=group and namespace=default when omitted.
 */
export function ownerRefToUrl(ownerRef: string): string {
  let kind = 'group';
  let rest = ownerRef;

  if (rest.includes(':')) {
    [kind, rest] = rest.split(':');
  }

  let namespace = 'default';
  let name = rest;

  if (rest.includes('/')) {
    [namespace, name] = rest.split('/');
  }

  return `/catalog/${namespace}/${kind.toLowerCase()}/${name}`;
}

/**
 * Normalise an owner/entity ref to a canonical, lowercased `kind:namespace/name`
 * so refs authored in shorthand (`devprod`, `group:devex`) compare equal to the
 * fully-qualified refs Backstage returns in `ownershipEntityRefs`. Defaults to
 * kind=group, namespace=default — matching {@link ownerRefToUrl}.
 */
export function normalizeOwnerRef(ownerRef: string): string {
  let kind = 'group';
  let rest = ownerRef.trim();

  if (rest.includes(':')) {
    [kind, rest] = rest.split(':');
  }

  let namespace = 'default';
  let name = rest;

  if (rest.includes('/')) {
    [namespace, name] = rest.split('/');
  }

  return `${kind.toLowerCase()}:${namespace.toLowerCase()}/${name.toLowerCase()}`;
}

/** Extract just the name portion from an owner ref, stripping kind and namespace. */
export function ownerDisplayName(ownerRef: string): string {
  let rest = ownerRef;
  if (rest.includes(':')) {
    rest = rest.split(':')[1];
  }
  if (rest.includes('/')) {
    rest = rest.split('/')[1];
  }
  return rest;
}

/** Strip the `url:` prefix from Backstage source-location annotation values */
export function parseSourceUrl(raw: string | undefined | null): string | null {
  if (!raw) return null;
  return raw.startsWith('url:') ? raw.slice(4) : raw;
}
